import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };

export type PackageSalesReportSearch = {
  dateFrom?: string;
  dateTo?: string;
  packageType?: "package" | "private_lesson" | "all";
  classTypeId?: string;
  levelId?: string;
  sortBy?: "count" | "revenue";
};

export function PackageSalesReportFilter() {
  const nav = useNavigate({ from: "/app/reports/package-sales" });
  const search = useSearch({
    from: "/app/reports/package-sales",
  }) as PackageSalesReportSearch;

  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");

  const [fromDate, setFromDate] = useState(search.dateFrom ?? defaultFrom);
  const [toDate, setToDate] = useState(search.dateTo ?? defaultTo);
  const [packageType, setPackageType] = useState<
    "package" | "private_lesson" | "all"
  >(search.packageType ?? "all");
  const [classTypeId, setClassTypeId] = useState(search.classTypeId ?? "");
  const [levelId, setLevelId] = useState(search.levelId ?? "");
  const [sortBy, setSortBy] = useState<"count" | "revenue">(
    search.sortBy ?? "count",
  );

  useEffect(() => {
    setFromDate(search.dateFrom ?? defaultFrom);
    setToDate(search.dateTo ?? defaultTo);
    setPackageType(search.packageType ?? "all");
    setClassTypeId(search.classTypeId ?? "");
    setLevelId(search.levelId ?? "");
    setSortBy(search.sortBy ?? "count");
  }, [
    search.dateFrom,
    search.dateTo,
    search.packageType,
    search.classTypeId,
    search.levelId,
    search.sortBy,
  ]);

  const showClassLevel = packageType === "package" || packageType === "all";

  const hasFilter =
    classTypeId ||
    levelId ||
    search.dateFrom ||
    search.dateTo ||
    search.packageType ||
    search.sortBy;

  const applyFilters = (updates: Partial<PackageSalesReportSearch>) => {
    const next = {
      dateFrom: updates.dateFrom ?? fromDate,
      dateTo: updates.dateTo ?? toDate,
      packageType: updates.packageType ?? packageType,
      classTypeId:
        updates.classTypeId !== undefined
          ? updates.classTypeId
          : classTypeId
            ? classTypeId
            : undefined,
      levelId:
        updates.levelId !== undefined
          ? updates.levelId
          : levelId
            ? levelId
            : undefined,
      sortBy: updates.sortBy ?? sortBy,
    };
    nav({ search: next });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີຈ່າຍ"
            className="h-8 w-full sm:w-fit"
          />

          <SimpleSelect
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "package", label: "ແພັກເກດຄລາດສ໌" },
              { value: "private_lesson", label: "ແພັກເກດຮຽນສ່ວນຕົວ" },
            ]}
            value={packageType}
            onValueChange={(val) => {
              setPackageType(val as "package" | "private_lesson" | "all");
              applyFilters({
                packageType: val as "package" | "private_lesson" | "all",
              });
            }}
            placeholder="ປະເພດແພັກເກດ"
            className="h-8 w-full sm:w-40"
          />

          <SimpleSelect
            value={packageType}
            onValueChange={(val) => {
              setPackageType(val as "package" | "private_lesson" | "all");
              applyFilters({
                packageType: val as "package" | "private_lesson" | "all",
              });
            }}
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "package", label: "ແພັກເກດຄລາດສ໌" },
              { value: "private_lesson", label: "ແພັກເກດຮຽນສ່ວນຕົວ" },
            ]}
          />

          {showClassLevel && (
            <>
              <InfiniteCombobox<ClassTypeItem>
                queryKey={["master", "class-types", "lookup"]}
                queryFn={({ search: q, pageParam }) =>
                  fetchLookupForInfinite<ClassTypeItem>(
                    `${config.apiUrl}/master/class-types/lookup`,
                    { search: q, pageParam },
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<ClassTypeItem>(
                    `${config.apiUrl}/master/class-types/lookup`,
                    id,
                  ).then((item) => item ?? null)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={classTypeId}
                onValueChange={(val) => {
                  setClassTypeId(val);
                  applyFilters({ classTypeId: val || undefined });
                }}
                placeholder="ປະເພດຄລາດສ໌"
                className="h-8 w-full sm:w-40"
                clearable
              />
              <InfiniteCombobox<LevelItem>
                queryKey={["master", "levels", "lookup"]}
                queryFn={({ search: q, pageParam }) =>
                  fetchLookupForInfinite<LevelItem>(
                    `${config.apiUrl}/master/levels/lookup`,
                    { search: q, pageParam },
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<LevelItem>(
                    `${config.apiUrl}/master/levels/lookup`,
                    id,
                  ).then((item) => item ?? null)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={levelId}
                onValueChange={(val) => {
                  setLevelId(val);
                  applyFilters({ levelId: val || undefined });
                }}
                placeholder="ລະດັບ"
                className="h-8 w-full sm:w-40"
                clearable
              />
            </>
          )}

          <SimpleSelect
            value={sortBy}
            onValueChange={(val) => {
              setSortBy(val as "count" | "revenue");
              applyFilters({ sortBy: val as "count" | "revenue" });
            }}
            className="h-8 w-full sm:w-40"
            options={[
              { value: "count", label: "ຈຳນວນທີ່ຂາຍ" },
              { value: "revenue", label: "ລາຍຮັບ" },
            ]}
            placeholder="ລາຍຮັບ"
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setClassTypeId("");
              setLevelId("");
              setFromDate(defaultFrom);
              setToDate(defaultTo);
              setPackageType("all");
              setSortBy("count");
              nav({
                search: {
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  packageType: "all",
                  sortBy: "count",
                },
              });
            }}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" />
            ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
